import Box from '@material-ui/core/Box';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import MaterialLink from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import Layout from '../components/Layout';
import * as Routes from '../constants/Routes';
import { usePackQuery, useAddPackMutation, useUpdatePackMutation } from '../queries';
import LoadingButton from '../components/LoadingButton';
import { FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import useDialog from '../hooks/useDialog';
import { PackDocument } from './PackAdd';
import PackDetails from '../components/pack/PackDetails';
import PackPartsList from '../components/pack/PackPartsList';
import PackDocumentation from '../components/pack/PackDocumentation';
import PackDocumentationUpload from '../components/pack/PackDocumentationUpload';
import { GraphQLError } from 'graphql';

type PackFormData = {
  // details
  id?: string;
  name?: string | null;
  code?: string | null;

  // parts list
  lockLatch?: string | null;
  closer?: string | null;
  intumescentStrip?: string | null;
  hinges?: string | null;
  handles?: string | null;
  smokeSeals?: string | null;
  dropSeal?: string | null;
  cylinder?: string | null;
  letterbox?: string | null;
  spyhole?: string | null;
  thresholdStrip?: string | null;
  keeps?: string | null;
  numerals?: string | null;
  chain?: string | null;
  weatherSeals?: string | null;
};
interface PackParams {
  id?: string;
}

export default function DoorModel() {
  const { showDialog } = useDialog();
  const [tabIndex, setTabIndex] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [documents, setDocuments] = useState<PackDocument[]>([]);
  const history = useHistory();
  const { id } = useParams<PackParams>();
  const [addPackMutation, { loading: addingPack }] = useAddPackMutation();
  const [updatePackMutation, { loading: updatingPack }] = useUpdatePackMutation();
  const saving = addingPack || updatingPack;

  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, error, refetch } = usePackQuery({
    variables: {
      id: id!,
    },
    skip: !Boolean(id),
  });

  useEffect(() => {
    if (!id) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }
  }, [id]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar('There was an error fetching this pack', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const form = useForm<PackFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (data?.pack && reset) {
      const newFormData: PackFormData = {
        id: data.pack.id,
        code: data.pack.code,
        name: data.pack.name,

        // parts
        lockLatch: data.pack.packParts?.lockLatch,
        closer: data.pack.packParts?.closer,
        intumescentStrip: data.pack.packParts?.intumescentStrip,
        hinges: data.pack.packParts?.hinges,
        handles: data.pack.packParts?.handles,
        smokeSeals: data.pack.packParts?.smokeSeals,
        dropSeal: data.pack.packParts?.dropSeal,
        cylinder: data.pack.packParts?.cylinder,
        letterbox: data.pack.packParts?.letterbox,
        spyhole: data.pack.packParts?.spyhole,
        thresholdStrip: data.pack.packParts?.thresholdStrip,
        // keeps?: string | null;
        // numerals?: string | null;
        // chain?: string | null;
        // weatherSeals?: string | null;
      };
      reset(newFormData);

      setDocuments(
        data.pack.documents?.map((p) => ({
          id: p.id,
          documentTypeCode: p.documentType.code,
          file: {
            name: p.originalFilename,
          },
        })) ?? []
      );
    }
  }, [data, reset]);

  const onCancelClicked = () => {
    showDialog({
      dialogText: 'Are you sure you want to cancel this operation?',
      okText: 'Yes',
      cancelText: 'No',
      onClose: () => {
        if (isEdit) {
          setIsEdit(false);
        } else {
          history.goBack();
        }
      },
    });
  };

  const onSubmit = async (formData: PackFormData) => {
    const packInput = {
      id: formData.id,
      code: formData.code!,
      name: formData.name!,
      lockLatch: formData.lockLatch,
      closer: formData.closer,
      intumescentStrip: formData.intumescentStrip,
      hinges: formData.hinges,
      handles: formData.handles,
      smokeSeals: formData.smokeSeals,
      dropSeal: formData.dropSeal,
      cylinder: formData.cylinder,
      letterbox: formData.letterbox,
      spyhole: formData.spyhole,
      thresholdStrip: formData.thresholdStrip,
      documents: documents.map((d) => ({
        documentTypeCode: d.documentTypeCode,
        id: d.id,
        file: d.id ? null : d.file, // send the file object for new files only or they will not match the FileUpload type
      })),
    };

    try {
      if (formData.id) {
        await updatePackMutation({
          variables: { packInput },
        });

        await refetch();
        setIsEdit(false);
      } else {
        const result = await addPackMutation({
          variables: { packInput },
        });

        const packId = result.data?.addPack.id;
        history.replace(`${Routes.Packs}/${packId}`);
        // Navigate to pack with id
      }
    } catch (e) {
      e.graphQLErrors.forEach((gqlerror: GraphQLError) => {
        if (gqlerror.extensions?.code === 'PACK_CODE_IN_USE') {
          enqueueSnackbar(`Pack code ${formData.code} is already in use`, { variant: 'error' });
        } else {
          enqueueSnackbar('There was an error saving this pack', { variant: 'error' });
        }
      });
    }
  };

  return (
    <Layout>
      <Grid container>
        <Grid item xs={2} />
        <Grid item xs={8}>
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Typography variant="h5">{data?.pack.name}</Typography>
              <Breadcrumbs aria-label="breadcrumb">
                <MaterialLink
                  color="inherit"
                  component="button"
                  variant="body2"
                  onClick={() => history.push(Routes.Packs)}
                >
                  <Typography variant="body2">Packs</Typography>
                </MaterialLink>
                <Typography variant="body2">{data?.pack.name}</Typography>
              </Breadcrumbs>
            </Box>
            <Box>
              {isEdit && (
                <Button variant="outlined" onClick={onCancelClicked}>
                  Cancel
                </Button>
              )}
            </Box>
          </Box>

          <Paper>
            <FormProvider {...form}>
              <form id="edit-pack" onSubmit={handleSubmit(onSubmit)}>
                <Box mt={3}>
                  <Tabs
                    value={tabIndex}
                    onChange={(_, index: number) => setTabIndex(index)}
                    aria-label="pack information"
                    indicatorColor="primary"
                    variant="fullWidth"
                  >
                    <Tab label="Details" />
                    <Tab label="Parts list" />
                    <Tab label="Documentation" />
                  </Tabs>
                  <Divider />
                  {loading && (
                    <Grid item container justify="center" alignItems="center" xs={12}>
                      <Box m={10}>
                        <CircularProgress />
                      </Box>
                    </Grid>
                  )}

                  {tabIndex === 0 && !loading && <PackDetails pack={data?.pack} isEdit={isEdit} />}
                  {tabIndex === 1 && !loading && <PackPartsList packParts={data?.pack.packParts} isEdit={isEdit} />}
                  {tabIndex === 2 && !loading && isEdit && (
                    <PackDocumentationUpload documents={documents} setDocuments={setDocuments} />
                  )}
                  {tabIndex === 2 && !loading && !isEdit && (
                    <PackDocumentation documents={data?.pack.documents} isEdit={isEdit} />
                  )}
                </Box>
                <Box display="flex" justifyContent="flex-end" p={5}>
                  {!isEdit && (
                    <Button variant="contained" color="primary" onClick={() => setIsEdit(true)}>
                      Edit
                    </Button>
                  )}
                  {isEdit && (
                    <LoadingButton
                      loading={saving}
                      disabled={documents.some((d) => !d.documentTypeCode) || saving}
                      variant="contained"
                      color="primary"
                      type="submit"
                      form="edit-pack"
                    >
                      Save
                    </LoadingButton>
                  )}
                </Box>
              </form>
            </FormProvider>
          </Paper>
        </Grid>
      </Grid>
    </Layout>
  );
}
